import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  IconButton,
  CircularProgress,
  Switch,
  Autocomplete,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import CategoryIcon from '@mui/icons-material/Category';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import PercentIcon from '@mui/icons-material/Percent';
import GroupsIcon from '@mui/icons-material/Groups';
import InboxIcon from '@mui/icons-material/Inbox';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { viewShiftAPICall } from '../../../API/Settings/Shift/viewShiftAPI';
import { 
  ListPosShifReportBillTypeSummary,
  ListPosShifReportDelePartnerSummary,
  ListPosShifReportDeleBoysSummary,
  ListPosShifReportDiscountSummary,
  ListPosShifReportCategorySalesSummary,
  ListPosShifReportDetails,
  ListPosShiftTaxDetails,
  ListPosCheckinSummaryAPI
} from '../../../component/Modules/PointOfSale/Report/ReportAPI';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import MobileShiftReportPDF from './MobileShiftReportPDF';
import './mobileReports.css';

const MobileShiftReport = ({ open, onClose, branchId: propBranchId, branchName: propBranchName }) => {
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [selectedShift, setSelectedShift] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    cashCard: true,
    sales: false,
    category: false,
    tax: false,
    delivery: false,
    discount: false,
    checkin: false
  });

  const reportContentRef = useRef(null);
  const printRef = useRef(null);
  const decimalPosition = Number(localStorage.getItem("decimalPosition")) || 2;
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const shiftStatus = useSelector((state) => state.shiftStatusSlice.value);
  const allShiftList = useSelector((state) => state.viewShiftSlice.value);
  
  // Use prop branchId if provided, otherwise fall back to userInfo
  const effectiveBranchId = propBranchId || userInfo?.branchPk || localStorage.getItem('branchId');
  
  const {
    shiftReportDetails,
    billTypeSummary,
    deliveryPartnerSummary,
    deliveryBoysSummary,
    discountSummary,
    categorySalesSummary,
    taxDetails,
    shiftReporCheckinSummary
  } = useSelector((state) => state.posReportshiftSlice);

  // Load shift list when dialog opens
  useEffect(() => {
    if (open && effectiveBranchId) {
      viewShiftAPICall({ branchId: effectiveBranchId });
      setSelectedShift(null); // Reset shift when branch changes
      // Auto-select current active shift only if no prop branchId
      if (!propBranchId && shiftStatus?._id) {
        setSelectedShift(shiftStatus);
      }
    }
  }, [open, effectiveBranchId]);

  // Fetch report when shift is selected
  useEffect(() => {
    if (open && selectedShift?._id) {
      fetchShiftReport(selectedShift._id);
    }
  }, [open, selectedShift?._id]);

  const handleShiftChange = (event, newValue) => {
    setSelectedShift(newValue);
  };

  const fetchShiftReport = async (shiftId) => {
    if (!shiftId || !effectiveBranchId) return;
    
    setLoading(true);
    try {
      const body = {
        branchId: effectiveBranchId,
        shiftId: shiftId
      };
      
      await Promise.all([
        ListPosShifReportBillTypeSummary(body),
        ListPosShifReportDelePartnerSummary(body),
        ListPosShifReportDeleBoysSummary(body).catch(() => {}), // Handle 404 gracefully
        ListPosShifReportDiscountSummary(body),
        ListPosShifReportCategorySalesSummary(body),
        ListPosShifReportDetails(body),
        ListPosShiftTaxDetails(body),
        ListPosCheckinSummaryAPI({ shiftId: shiftId })
      ]);
    } catch (error) {
      console.error('Error fetching shift report:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(decimalPosition);
  };

  // Expand all sections for PDF generation
  const expandAllSections = () => {
    setExpandedSections({
      cashCard: true,
      sales: true,
      category: true,
      tax: true,
      delivery: true,
      discount: true,
      checkin: true
    });
  };

  // Generate PDF and show preview using professional template
  const generatePdfPreview = async () => {
    if (!printRef.current) return;
    
    setPdfLoading(true);
    
    // Wait for DOM to update
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add more pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Store PDF data for preview and sharing
      const blob = pdf.output('blob');
      const dataUrl = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfDataUrl(dataUrl);
      setShowPdfPreview(true);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  // Print using react-to-print
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `
  });

  // Share PDF
  const sharePDF = async () => {
    if (!pdfBlob) return;
    
    const shiftName = selectedShift?.shiftSeq || `Shift_${selectedShift?.shiftId}`;
    const fileName = `ShiftReport_${shiftName}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Check if Web Share API is available (mobile)
    if (navigator.share && navigator.canShare) {
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `Shift Report - ${shiftName}`,
            text: 'Shift Report'
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error sharing:', error);
          }
        }
      } else {
        downloadPDF();
      }
    } else {
      downloadPDF();
    }
  };

  // Download PDF
  const downloadPDF = () => {
    if (!pdfDataUrl) return;
    
    const shiftName = selectedShift?.shiftSeq || `Shift_${selectedShift?.shiftId}`;
    const fileName = `ShiftReport_${shiftName}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Close PDF preview
  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfDataUrl) {
      URL.revokeObjectURL(pdfDataUrl);
    }
    setPdfDataUrl(null);
    setPdfBlob(null);
  };

  const renderCashCardSection = () => {
    const data = billTypeSummary?.cashAndCard;
    if (!data?.list?.length) return null;

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header payment"
          onClick={() => toggleSection('cashCard')}
        >
          <h4><PaymentsIcon /> Cash & Card</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(data?.total)}</span>
            {expandedSections.cashCard ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.cashCard ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Method</th>
                <th className="text-right">Amount</th>
                {showDetails && <th className="text-right">Comm.</th>}
                {showDetails && <th className="text-right">Balance</th>}
              </tr>
            </thead>
            <tbody>
              {data?.list?.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.type}</td>
                  <td className="text-right">{formatAmount(item.amount)}</td>
                  {showDetails && <td className="text-right">{formatAmount(item.commission)}</td>}
                  {showDetails && <td className="text-right">{formatAmount(item.balance)}</td>}
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                <td className="text-right">{formatAmount(data?.total)}</td>
                {showDetails && <td className="text-right">-</td>}
                {showDetails && <td className="text-right">-</td>}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSalesSection = () => {
    if (!billTypeSummary) return null;

    const { dineIn, takeAway, delivery, selfDelivery, aggregator } = billTypeSummary;
    const totalSales = (dineIn?.total || 0) + (takeAway?.total || 0) + (delivery?.total || 0);

    const billTypes = [
      { name: 'Dine In', data: dineIn },
      { name: 'Take Away', data: takeAway },
      { name: 'Self Delivery', data: selfDelivery },
      { name: 'Aggregator', data: aggregator }
    ].filter(bt => bt.data?.total > 0 || bt.data?.list?.length > 0);

    if (!billTypes.length) return null;

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header sales"
          onClick={() => toggleSection('sales')}
        >
          <h4><ReceiptLongIcon /> Bill Type Summary</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(totalSales)}</span>
            {expandedSections.sales ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.sales ? 'expanded' : ''}`}>
          {billTypes.map((billType, idx) => (
            <div key={idx} className="mobile-report-subsection">
              <div className="subsection-header">
                <span>{billType.name}</span>
                <span>{formatAmount(billType.data?.total)}</span>
              </div>
              {showDetails && billType.data?.list?.length > 0 && (
                <table className="mobile-report-table detail-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billType.data.list.map((order, oIdx) => (
                      <tr key={oIdx}>
                        <td>{order.billNo}</td>
                        <td>{order.date}</td>
                        <td className="text-right">{formatAmount(order.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          <div className="subsection-total">
            <span>Total Sales</span>
            <span>{formatAmount(totalSales)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderCategorySection = () => {
    if (!categorySalesSummary?.length) return null;

    const total = categorySalesSummary.reduce((sum, cat) => sum + (cat.totalAmount || cat.total || 0), 0);

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header category"
          onClick={() => toggleSection('category')}
        >
          <h4><CategoryIcon /> Category Sales</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(total)}</span>
            {expandedSections.category ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.category ? 'expanded' : ''}`}>
          {categorySalesSummary.map((cat, idx) => (
            <div key={idx} className="mobile-report-subsection">
              <div className="subsection-header">
                <span>{cat.categoryName || cat._id}</span>
                <span>{formatAmount(cat.totalAmount || cat.total)}</span>
              </div>
              {showDetails && cat.products?.length > 0 && (
                <table className="mobile-report-table detail-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.products.map((prod, pIdx) => (
                      <tr key={pIdx}>
                        <td>{prod.product || prod.productName}</td>
                        <td className="text-center">{prod.quantity || prod.qty || 0}</td>
                        <td className="text-right">{formatAmount(prod.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          <div className="subsection-total">
            <span>Category Total</span>
            <span>{formatAmount(total)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderTaxSection = () => {
    if (!taxDetails?.length) return null;

    const total = taxDetails.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header tax"
          onClick={() => toggleSection('tax')}
        >
          <h4><PercentIcon /> Tax Details</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(total)}</span>
            {expandedSections.tax ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.tax ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Tax Name</th>
                {showDetails && <th className="text-center">%</th>}
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {taxDetails.map((tax, idx) => (
                <tr key={idx}>
                  <td>{tax.taxName || tax._id}</td>
                  {showDetails && <td className="text-center">{tax.taxPercentage || '-'}</td>}
                  <td className="text-right">{formatAmount(tax.taxAmount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                {showDetails && <td className="text-center">-</td>}
                <td className="text-right">{formatAmount(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDeliverySection = () => {
    const hasPartners = deliveryPartnerSummary?.length > 0;
    const hasBoys = deliveryBoysSummary?.length > 0;
    
    if (!hasPartners && !hasBoys) return null;

    const partnerTotal = deliveryPartnerSummary?.reduce((sum, p) => sum + (p.total || 0), 0) || 0;
    const boysTotal = deliveryBoysSummary?.reduce((sum, b) => sum + (b.total || 0), 0) || 0;

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header delivery"
          onClick={() => toggleSection('delivery')}
        >
          <h4><DeliveryDiningIcon /> Delivery Summary</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(partnerTotal + boysTotal)}</span>
            {expandedSections.delivery ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.delivery ? 'expanded' : ''}`}>
          {hasPartners && (
            <>
              <p style={{ fontWeight: 600, marginBottom: 8, color: '#666' }}>Delivery Partners</p>
              {deliveryPartnerSummary.map((partner, idx) => (
                <div key={idx} className="mobile-report-subsection">
                  <div className="subsection-header">
                    <span>{partner.partner || partner.name || partner._id}</span>
                    <span>{formatAmount(partner.total)}</span>
                  </div>
                  {showDetails && partner.data?.length > 0 && (
                    <table className="mobile-report-table detail-table">
                      <thead>
                        <tr>
                          <th>Order No</th>
                          <th>Date</th>
                          <th className="text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partner.data.map((order, oIdx) => (
                          <tr key={oIdx}>
                            <td>{order.orderNo || order.billNo}</td>
                            <td>{order.date}</td>
                            <td className="text-right">{formatAmount(order.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </>
          )}
          {hasBoys && (
            <>
              <p style={{ fontWeight: 600, margin: '12px 0 8px', color: '#666' }}>Delivery Boys</p>
              {deliveryBoysSummary.map((boy, idx) => (
                <div key={idx} className="mobile-report-subsection">
                  <div className="subsection-header">
                    <span>{boy.partner || boy.name || boy._id}</span>
                    <span>{formatAmount(boy.total)}</span>
                  </div>
                  {showDetails && boy.data?.length > 0 && (
                    <table className="mobile-report-table detail-table">
                      <thead>
                        <tr>
                          <th>Order No</th>
                          <th>Date</th>
                          <th className="text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boy.data.map((order, oIdx) => (
                          <tr key={oIdx}>
                            <td>{order.orderNo || order.billNo}</td>
                            <td>{order.date}</td>
                            <td className="text-right">{formatAmount(order.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDiscountSection = () => {
    if (!discountSummary?.length) return null;

    const total = discountSummary.reduce((sum, d) => sum + (d.total || d.amount || 0), 0);

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header discount"
          onClick={() => toggleSection('discount')}
        >
          <h4><LocalOfferIcon /> Discounts</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(total)}</span>
            {expandedSections.discount ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.discount ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Discount</th>
                {showDetails && <th className="text-center">Count</th>}
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {discountSummary.map((disc, idx) => (
                <tr key={idx}>
                  <td>{disc.name || disc._id || 'Discount'}</td>
                  {showDetails && <td className="text-center">{disc.count || 0}</td>}
                  <td className="text-right">{formatAmount(disc.total || disc.amount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                {showDetails && <td className="text-center">-</td>}
                <td className="text-right">{formatAmount(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCheckinSection = () => {
    if (!shiftReporCheckinSummary?.length) return null;

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header"
          onClick={() => toggleSection('checkin')}
        >
          <h4><GroupsIcon /> Check-in Summary</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{shiftReporCheckinSummary.length}</span>
            {expandedSections.checkin ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.checkin ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th className="text-center">In</th>
                <th className="text-center">Out</th>
              </tr>
            </thead>
            <tbody>
              {shiftReporCheckinSummary.map((emp, idx) => (
                <tr key={idx}>
                  <td>{emp.employeeName || emp.name}</td>
                  <td className="text-center">{emp.checkInTime || '-'}</td>
                  <td className="text-center">{emp.checkOutTime || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="mobile-report-dialog"
    >
      <div className="mobile-report-header">
        <h3>Advance Shift Report</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconButton 
            className="share-btn" 
            onClick={generatePdfPreview} 
            size="small"
            disabled={pdfLoading || !selectedShift}
            title="Print Preview"
          >
            {pdfLoading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
          </IconButton>
          <IconButton className="close-btn" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="mobile-report-container">
        {/* Shift Selector */}
        <div className="mobile-report-filters">
          <Autocomplete
            fullWidth
            size="small"
            options={allShiftList || []}
            getOptionLabel={(option) => `${option.shiftSeq || `Shift #${option.shiftId}`} - ${option.startDate || 'Active'}`}
            value={selectedShift}
            onChange={handleShiftChange}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            renderInput={(params) => (
              <TextField {...params} label="Select Shift" />
            )}
          />
        </div>

        {loading ? (
          <div className="mobile-report-loading">
            <CircularProgress size={40} />
            <p>Loading report...</p>
          </div>
        ) : !selectedShift ? (
          <div className="mobile-report-empty">
            <InboxIcon />
            <p>Select a shift to view report.</p>
          </div>
        ) : (
          <div ref={reportContentRef} className="report-pdf-content">
            {/* Shift Info Banner */}
            <div className="shift-info-banner">
              <div className="shift-id">
                Shift {shiftReportDetails?.shiftSeq || selectedShift?.shiftSeq || `#${selectedShift?.shiftId}`}
                {!(shiftReportDetails?.endDate || selectedShift?.endDate) && (
                  <span style={{ marginLeft: 8, padding: '2px 8px', backgroundColor: '#11998e', color: 'white', borderRadius: 4, fontSize: '0.7rem' }}>ACTIVE</span>
                )}
              </div>
              <div className="shift-details" style={{ flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ color: '#666' }}>Started by:</span>
                  <span>{shiftReportDetails?.startedBy || selectedShift?.employeeName || userInfo?.userName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ color: '#666' }}>Start:</span>
                  <span>{shiftReportDetails?.startDate || selectedShift?.startDate ? 
                    new Date(shiftReportDetails?.startDate || selectedShift?.startDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</span>
                </div>
                {(shiftReportDetails?.endDate || selectedShift?.endDate) && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span style={{ color: '#666' }}>Ended by:</span>
                      <span>{shiftReportDetails?.endedBy || '-'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span style={{ color: '#666' }}>End:</span>
                      <span>{new Date(shiftReportDetails?.endDate || selectedShift?.endDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Details Toggle */}
            <div className="details-toggle">
              <span className="toggle-label">Show Details</span>
              <Switch
                checked={showDetails}
                onChange={(e) => setShowDetails(e.target.checked)}
                color="primary"
              />
            </div>

            {/* Report Sections */}
            {renderCashCardSection()}
            {renderSalesSection()}
            {renderCategorySection()}
            {renderTaxSection()}
            {renderDeliverySection()}
            {renderDiscountSection()}
            {renderCheckinSection()}

            {/* Empty state if no data */}
            {!billTypeSummary && !categorySalesSummary?.length && (
              <div className="mobile-report-empty">
                <InboxIcon />
                <p>No data available for this shift.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PDF Preview Dialog */}
      <Dialog
        open={showPdfPreview}
        onClose={closePdfPreview}
        fullWidth
        maxWidth="md"
        className="pdf-preview-dialog"
      >
        <div className="pdf-preview-header">
          <h3>Print Preview</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton 
              onClick={handlePrint} 
              size="small"
              title="Print"
              className="pdf-action-btn"
            >
              <PrintIcon />
            </IconButton>
            <IconButton 
              onClick={sharePDF} 
              size="small"
              title="Share"
              className="pdf-action-btn"
            >
              <ShareIcon />
            </IconButton>
            <IconButton 
              onClick={downloadPDF} 
              size="small"
              title="Download"
              className="pdf-action-btn"
            >
              <PictureAsPdfIcon />
            </IconButton>
            <IconButton onClick={closePdfPreview} size="small" className="close-btn">
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className="pdf-preview-content">
          {pdfDataUrl && (
            <iframe
              src={pdfDataUrl}
              title="PDF Preview"
              className="pdf-iframe"
            />
          )}
        </div>
      </Dialog>

      {/* Hidden Professional PDF Template for printing */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <MobileShiftReportPDF ref={printRef} selectedShift={selectedShift} showDetails={showDetails} />
      </div>
    </Dialog>
  );
};

export default MobileShiftReport;
